import React from 'react';
import { View, StyleSheet, Pressable, StyleProp, ViewStyle } from 'react-native';
import { Bevel } from './Bevel';
import { Text } from './Text';
import { makeStyles, useTheme } from './ThemeContext';
import type { Theme } from './themes';

export interface TitleBarProps {
  title: string;
  active?: boolean;
  onClose?: () => void;
  onMinimize?: () => void;
  onMaximize?: () => void;
  /** Small glyph slot beside the title, e.g. the running-man icon. */
  icon?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

/**
 * The window caption.
 *
 * Windows themes draw a left-to-right colour fade with the title on the left
 * and the buttons on the right. Platinum draws horizontal pinstripes with the
 * title centred and the close box on the left, which is most of what makes a
 * Mac window look like a Mac window.
 */
export function TitleBar({
  title,
  active = true,
  onClose,
  onMinimize,
  onMaximize,
  icon,
  style,
}: TitleBarProps) {
  const theme = useTheme();
  const styles = useStyles();
  const c = theme.colors;

  const from = active ? c.titleActiveLeft : c.titleInactiveLeft;
  const to = active ? c.titleActiveRight : c.titleInactiveRight;
  const textColor = active ? c.titleText : c.titleTextInactive;

  const buttons = (
    <View style={styles.buttons}>
      {onMinimize ? <CaptionButton glyph="minimize" onPress={onMinimize} label="Minimize" /> : null}
      {onMaximize ? <CaptionButton glyph="maximize" onPress={onMaximize} label="Maximize" /> : null}
      {onClose ? <CaptionButton glyph="close" onPress={onClose} label="Close" /> : null}
    </View>
  );

  const buttonsLeft = theme.titleBar.buttons === 'left';
  const centered = theme.titleBar.align === 'center';

  return (
    <View style={[styles.root, style]}>
      {theme.titleBar.style === 'pinstripe' ? (
        <Pinstripes theme={theme} active={active} />
      ) : (
        <GradientBands from={from} to={to} />
      )}

      <View style={styles.content}>
        {buttonsLeft ? buttons : null}
        {icon && !centered ? <View style={styles.icon}>{icon}</View> : null}
        <Text
          numberOfLines={1}
          bold
          style={[
            styles.title,
            { color: textColor, fontSize: theme.fontSizes.title },
            centered && styles.titleCentered,
          ]}
        >
          {title}
        </Text>
        {/* Balances the centred title against the button cluster, so it sits
            in the middle of the bar rather than the middle of what is left. */}
        {centered && buttonsLeft ? <View style={styles.buttonSpacer} /> : null}
        {!buttonsLeft ? buttons : null}
      </View>
    </View>
  );
}

const BAND_COUNT = 64;

/**
 * The caption gradient, faked with overlapping solid bands because RN has no
 * gradient primitive without a native dependency. Bands are drawn wider than
 * their slot so neighbours overlap; laying them out as flex siblings leaves
 * hairline seams wherever an edge lands between physical pixels.
 */
function GradientBands({ from, to }: { from: string; to: string }) {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const step = 100 / BAND_COUNT;
  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      {Array.from({ length: BAND_COUNT }, (_, i) => {
        const t = i / (BAND_COUNT - 1);
        const c = `rgb(${mix(a[0], b[0], t)},${mix(a[1], b[1], t)},${mix(a[2], b[2], t)})`;
        return (
          <View
            key={i}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${i * step}%`,
              width: `${step * 1.5}%`,
              backgroundColor: c,
            }}
          />
        );
      })}
    </View>
  );
}

/** Platinum's caption: alternating 1px rules over the face colour. */
function Pinstripes({ theme, active }: { theme: Theme; active: boolean }) {
  const rows = Math.floor(theme.metrics.titleBarHeight / 2);
  const base = active ? theme.colors.titleActiveLeft : theme.colors.titleInactiveLeft;
  const line = active ? theme.colors.titleActiveRight : theme.colors.titleInactiveRight;
  return (
    <View style={[StyleSheet.absoluteFill, { backgroundColor: base }]} pointerEvents="none">
      {Array.from({ length: rows }, (_, i) => (
        <View
          key={i}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: i * 2 + 1,
            height: 1,
            backgroundColor: line,
          }}
        />
      ))}
    </View>
  );
}

const mix = (x: number, y: number, t: number) => Math.round(x + (y - x) * t);

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function CaptionButton({
  glyph,
  onPress,
  label,
}: {
  glyph: 'close' | 'minimize' | 'maximize';
  onPress: () => void;
  label: string;
}) {
  const styles = useStyles();
  const theme = useTheme();
  const [held, setHeld] = React.useState(false);
  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => setHeld(true)}
      onPressOut={() => setHeld(false)}
      accessibilityRole="button"
      accessibilityLabel={label}
      hitSlop={8}
    >
      <Bevel
        variant={held ? 'pressed' : 'raised'}
        style={styles.captionButton}
        contentStyle={styles.captionInner}
      >
        {/* Platinum's boxes are empty until hovered; Windows draws a glyph. */}
        {theme.titleBar.style === 'pinstripe' ? null : <Glyph kind={glyph} color={theme.colors.windowText} />}
      </Bevel>
    </Pressable>
  );
}

/** The caption glyphs are 1px shapes, so they are drawn as views, not text. */
function Glyph({ kind, color }: { kind: 'close' | 'minimize' | 'maximize'; color: string }) {
  if (kind === 'minimize') {
    return <View style={{ width: 6, height: 2, backgroundColor: color, marginTop: 4 }} />;
  }
  if (kind === 'maximize') {
    return (
      <View style={{ width: 8, height: 7, borderWidth: 1, borderTopWidth: 2, borderColor: color }} />
    );
  }
  return (
    <View style={{ width: 8, height: 8, alignItems: 'center', justifyContent: 'center' }}>
      <View style={[stroke(color), { transform: [{ rotate: '45deg' }] }]} />
      <View style={[stroke(color), { transform: [{ rotate: '-45deg' }] }]} />
    </View>
  );
}

const stroke = (color: string) =>
  ({ position: 'absolute', top: 3, left: -1, width: 9, height: 1, backgroundColor: color }) as const;

const useStyles = makeStyles((t) => ({
  root: { height: t.metrics.titleBarHeight, justifyContent: 'center' as const },
  content: { flexDirection: 'row' as const, alignItems: 'center' as const, paddingHorizontal: 3, gap: 3 },
  icon: { width: 16, height: 16, justifyContent: 'center' as const, alignItems: 'center' as const },
  title: { flex: 1, lineHeight: t.fontSizes.title + 3 },
  titleCentered: { textAlign: 'center' as const },
  buttons: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 2 },
  // Same width as a single caption button, to balance a centred title.
  buttonSpacer: { width: t.metrics.titleButton },
  captionButton: { width: t.metrics.titleButton, height: t.titleBar.style === 'pinstripe' ? 13 : 14 },
  captionInner: { alignItems: 'center' as const, justifyContent: 'center' as const },
}));
